import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import type { ComponentSize } from '@/types/general';
import { getComponentSize } from '@/types/general';

// Translation key for the spinner's accessible label. `form.loading` resolves to
// "Loading..." in English and is the closest existing key for an indeterminate
// busy state. Kept in a constant so callers see one source of truth.
const SPINNER_LABEL_KEY = 'form.loading';

// Thin circular spinner per DESIGN.md:
// - 1.5px stroke, `currentColor`, primary by default.
// - 800ms rotation with emphasized easing (set via inline style on the svg).
// - No icon font, no heavy branded treatment.
// - `motion-reduce:hidden` so users with reduced-motion preferences
//   don't see a perpetually spinning element. Callers should provide
//   an alternative (skeleton, status text) for that audience.
const spinnerVariants = cva(
  'inline-block animate-spin motion-reduce:hidden',
  {
    variants: {
      variant: {
        // Default variant
        'default': 'text-primary',

        // Primary variants
        'primary': 'text-primary',
        'primary-light': 'text-primary-light',
        'primary-dark': 'text-primary-dark',

        // Secondary variants
        'secondary': 'text-secondary',
        'secondary-light': 'text-secondary-light',
        'secondary-dark': 'text-secondary-dark',

        // Info variants
        'info': 'text-info',
        'info-light': 'text-info-light',
        'info-dark': 'text-info-dark',

        // Success variants
        'success': 'text-success',
        'success-light': 'text-success-light',
        'success-dark': 'text-success-dark',

        // Warning variants
        'warning': 'text-warning',
        'warning-light': 'text-warning-light',
        'warning-dark': 'text-warning-dark',

        // Error variants
        'error': 'text-error',
        'error-light': 'text-error-light',
        'error-dark': 'text-error-dark',

        // Action variants
        'action': 'text-action',
        'action-selected': 'text-action-selected',

        // Inherit color from parent
        'inherit': 'text-inherit',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type SpinnerLoaderProps = {
  size?: ComponentSize;
} & React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof spinnerVariants>;

export default function SpinnerLoader({
  size = 'sm',
  variant,
  className,
  ...rest
}: SpinnerLoaderProps) {
  const pixelSize = getComponentSize(size);
  const translate = useTranslations();

  return (
    <div
      style={{ width: pixelSize, height: pixelSize }}
      className={cn('inline-flex items-center justify-center', className)}
      role="status"
      aria-label={translate(SPINNER_LABEL_KEY)}
      aria-live="polite"
      {...rest}
    >
      <svg
        className={cn(spinnerVariants({ variant }))}
        // 800ms emphasized rotation per DESIGN.md.
        style={{
          animationDuration: '800ms',
          animationTimingFunction: 'var(--ease-emphasized)',
        }}
        width={pixelSize}
        height={pixelSize}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray="42 18"
          opacity="0.9"
        />
      </svg>
    </div>
  );
}
